import {get} from "lodash";
import {getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated with this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'externalQuestionnaires';

// Define reducer types handled by this reducer
export const REDUCER_TYPES = {
	RESET: '@externalQuestionnaires/reset',
	SET_LIST_DATA: '@externalQuestionnaires/set_list_data',
	CLEAR_LIST_DATA: '@externalQuestionnaires/clear_list_data',
	SET_ITEM: '@externalQuestionnaires/set_item',
	SET_ITEM_QUESTIONNAIRES_LIST_DATA: '@externalQuestionnaires/set_item_questionnaires_list_data',
	CLEAR_ITEM_QUESTIONNAIRES_LIST_DATA: '@externalQuestionnaires/clear_item_questionnaires_list_data',
	CLEAR_ITEM: '@externalQuestionnaires/clear_item',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} externalQuestionnairesListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setExternalQuestionnairesListData: externalQuestionnairesListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: externalQuestionnairesListData
	}),
	clearExternalQuestionnairesListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {ExternalQuestionnairesListItemDataObject} externalQuestionnairesItem
	 * @return {{type: string, item: ExternalQuestionnairesListItemDataObject}}
	 */
	setExternalQuestionnairesItem: externalQuestionnairesItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: externalQuestionnairesItem
	}),
	clearExternalQuestionnairesItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),

	setItemQuestionnairesListData: itemQuestionnairesListData => ({
		type: REDUCER_TYPES.SET_ITEM_QUESTIONNAIRES_LIST_DATA, 
		itemQuestionnairesListData
	}),
	clearItemQuestionnairesListData: () => ({type: REDUCER_TYPES.CLEAR_ITEM_QUESTIONNAIRES_LIST_DATA}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export an initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {ExternalQuestionnairesListItemDataObject}
	 */
	item: null,

	/** @type {IoJsonFetchResponseObject} */
	itemQuestionnairesListData: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};
		
		case REDUCER_TYPES.SET_ITEM_QUESTIONNAIRES_LIST_DATA: return {
			...state, itemQuestionnairesListData: action.itemQuestionnairesListData
		};
		case REDUCER_TYPES.CLEAR_ITEM_QUESTIONNAIRES_LIST_DATA: return {
			...state, itemQuestionnairesListData: initialState.itemQuestionnairesListData
		};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getExternalQuestionnairesList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getExternalQuestionnairesListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getExternalQuestionnairesListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getExternalQuestionnairesListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getExternalQuestionnairesItem: state => get(state, [reducerStoreKey, 'item']),
	
	getExternalQuestionnairesItemQuestionnairesList: state => get(
		state, [reducerStoreKey, 'itemQuestionnairesListData', 'data']
	),
	getExternalQuestionnairesItemQuestionnairesListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'itemQuestionnairesListData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'itemQuestionnairesListData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'itemQuestionnairesListData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'itemQuestionnairesListData', 'isLastPage'])
	}),
	getExternalQuestionnairesItemQuestionnairesListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'itemQuestionnairesListData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'itemQuestionnairesListData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getExternalQuestionnairesItemQuestionnairesListFilter: state => get(
		state, [reducerStoreKey, 'itemQuestionnairesListData', 'filter'], null
	),
};

export default reducer;